/**
 * Map auth API error codes to professional, user-facing messages.
 * Backend returns { success: false, code, message }. Prefer message when safe; else use code mapping.
 * Never show raw axios/fetch errors or status codes to users.
 */
const CODE_TO_MESSAGE = {
  // Login
  INVALID_CREDENTIALS: "Incorrect email or password.",
  GOOGLE_ACCOUNT: "GOOGLE_ACCOUNT",
  INVALID_INPUT: "Please check your input and try again.",
  LOGIN_FAILED: "Something went wrong. Please try again.",
  // Session / me
  NOT_AUTHENTICATED: "Your session expired. Please log in again.",
  SESSION_EXPIRED: "Your session expired. Please log in again.",
  INTERNAL_ERROR: "Something went wrong. Please try again.",
  // OTP / signup
  OTP_SENT: "We've sent a verification code to your mobile number.",
  OTP_INVALID: "The code you entered is incorrect. Please try again.",
  OTP_EXPIRED: "Your code has expired. Please request a new one.",
  OTP_QUOTA_EXCEEDED: "SMS limit reached. Please try again later.",
  OTP_INVALID_PHONE: "Invalid phone or email. Use a valid 10-digit number or email.",
  OTP_SERVICE_UNAVAILABLE: "OTP service is temporarily unavailable. Please try again later.",
  FIREBASE_NOT_CONFIGURED: "OTP service is temporarily unavailable. Please try again later.",
  FIREBASE_KEY_INVALID: "OTP service configuration error. Please try again later.",
  OTP_SEND_FAILED: "OTP service is temporarily unavailable. Please try again later.",
  USER_ALREADY_EXISTS: "An account with this email already exists.",
  EMAIL_EXISTS: "An account with this email already exists.",
  USERNAME_EXISTS: "Username already taken.",
  PHONE_EXISTS: "An account with this phone already exists.",
  INVALID_OTP: "The code you entered is incorrect. Please try again.",
  VERIFICATION_REQUIRED: "Complete OTP verification and try again.",
  VALIDATION_ERROR: "Please check your input and try again.",
  SIGNUP_FAILED: "Something went wrong. Please try again.",
  INVALID_PHONE: "Invalid phone or email. Use a valid 10-digit number or email.",
  // Legacy / reset
  USER_NOT_FOUND: "Account not found.",
  INVALID_PASSWORD: "Incorrect password.",
  ACCOUNT_DISABLED: "Account is not set up for login. Please contact support.",
  UNAUTHORIZED: "Please log in again.",
  RESET_FAILED: "Something went wrong. Please try again.",
};

const FALLBACK = "Something went wrong. Please try again.";

/**
 * @param {object} error - Thrown from apiFetch (has status, data) or axios (has response.data)
 * @returns {string} User-facing message (no technical jargon, no raw 401/500)
 */
export function mapErrorToUserMessage(error) {
  if (!error) return FALLBACK;

  const data = error.data ?? error.response?.data;
  const code = data?.errorCode ?? data?.code;
  if (code && CODE_TO_MESSAGE[code]) return CODE_TO_MESSAGE[code];
  if (code === 'INVALID_CREDENTIALS') return "Invalid email or password.";

  const message = data?.message ?? data?.error;
  if (typeof message === "string" && message.length > 0 && message.length < 200 && !message.startsWith("{")) {
    return message;
  }

  if (error.status === 503) return CODE_TO_MESSAGE.OTP_SERVICE_UNAVAILABLE || FALLBACK;
  if (error.status === 401) return "Your session expired. Please log in again.";
  if (error.status === 500) return FALLBACK;

  const msg = error.message;
  if (typeof msg === "string") {
    if (msg.includes("401") || msg.includes("Unauthorized")) return "Your session expired. Please log in again.";
    if (msg.includes("500") || msg.includes("Network")) return FALLBACK;
    if (msg.length < 120 && !msg.startsWith("{")) return msg;
  }

  return FALLBACK;
}

export default mapErrorToUserMessage;
